// Redux slice for image state management
const initialState = {
  uploadedImages: [],
  currentImage: null,
  variations: [],
  editHistory: [],
  currentEditVersion: null,
  loading: false,
  error: null,
  
  // Retry status tracking
  isRetrying: false,
  retryAttempt: 0,
  maxRetries: 3,
  retryReason: '',
  retryOperation: null
};

// Action types
const UPLOAD_IMAGE_SUCCESS = 'images/uploadImageSuccess';
const SET_CURRENT_IMAGE = 'images/setCurrentImage';
const SET_VARIATIONS = 'images/setVariations';
const ADD_EDIT_VERSION = 'images/addEditVersion';
const SET_CURRENT_EDIT_VERSION = 'images/setCurrentEditVersion';
const SET_LOADING = 'images/setLoading';
const SET_ERROR = 'images/setError';
const CLEAR_ERROR = 'images/clearError';
const RESET_STATE = 'images/resetState';
const SET_RETRY_STATUS = 'images/setRetryStatus';
const CLEAR_RETRY_STATUS = 'images/clearRetryStatus';

// Action creators
export const uploadImageSuccess = (imageData) => ({
  type: UPLOAD_IMAGE_SUCCESS,
  payload: imageData
});

export const setCurrentImage = (image) => ({
  type: SET_CURRENT_IMAGE,
  payload: image
});

export const setVariations = (variations) => ({
  type: SET_VARIATIONS,
  payload: variations
});

export const addEditVersion = (editVersion) => ({
  type: ADD_EDIT_VERSION,
  payload: editVersion
});

export const setCurrentEditVersion = (versionId) => ({
  type: SET_CURRENT_EDIT_VERSION,
  payload: versionId
});

export const setLoading = (isLoading) => ({
  type: SET_LOADING,
  payload: isLoading
});

export const setError = (error) => ({
  type: SET_ERROR,
  payload: error
});

export const clearError = () => ({
  type: CLEAR_ERROR
});

export const resetState = () => ({
  type: RESET_STATE
});

/**
 * Sets the retry status for operations that need to be retried
 * 
 * @param {Object} retryStatus - The retry status details
 * @param {boolean} retryStatus.isRetrying - Whether a retry is in progress
 * @param {number} retryStatus.retryAttempt - Current retry attempt number
 * @param {string} retryStatus.retryReason - Error message that caused the retry
 * @param {string} retryStatus.retryOperation - Name of operation being retried
 * @param {number} retryStatus.maxRetries - Maximum number of retry attempts
 */
export const setRetryStatus = (retryStatus) => ({
  type: SET_RETRY_STATUS,
  payload: retryStatus
});

export const clearRetryStatus = () => ({
  type: CLEAR_RETRY_STATUS
});

// Reducer
const imageReducer = (state = initialState, action) => {
  switch (action.type) {
    case UPLOAD_IMAGE_SUCCESS:
      return {
        ...state,
        uploadedImages: [...state.uploadedImages, action.payload],
        currentImage: action.payload,
        // Initialize edit history with the uploaded image
        editHistory: state.editHistory.length === 0 ? 
          [{ 
            id: 1, 
            description: 'Original upload', 
            image: action.payload.imageUrl,
            base64Data: action.payload.base64Data,
            blobUrl: action.payload.blobUrl || action.payload.imageUrl,
            path: action.payload.filePath,
            timestamp: 'Just now' 
          }] : state.editHistory,
        currentEditVersion: state.editHistory.length === 0 ? 1 : state.currentEditVersion
      };
    
    case SET_CURRENT_IMAGE:
      return {
        ...state,
        currentImage: action.payload
      };
    
    case SET_VARIATIONS:
      return {
        ...state,
        variations: action.payload
      };
    
    case ADD_EDIT_VERSION:
      return {
        ...state,
        editHistory: [...state.editHistory, {
          ...action.payload,
          id: state.editHistory.length + 1
        }],
        currentEditVersion: state.editHistory.length + 1
      };
    
    case SET_CURRENT_EDIT_VERSION:
      return {
        ...state,
        currentEditVersion: action.payload
      };
    
    case SET_LOADING:
      return {
        ...state,
        loading: action.payload,
        // If loading is false, also clear retry status
        ...(action.payload === false ? {
          isRetrying: false,
          retryAttempt: 0,
          retryReason: '',
          retryOperation: null
        } : {})
      };
    
    case SET_ERROR:
      return {
        ...state,
        error: action.payload,
        // When setting an error, clear retry status
        isRetrying: false,
        retryAttempt: 0,
        retryReason: '',
        retryOperation: null
      };
    
    case CLEAR_ERROR:
      return {
        ...state,
        error: null
      };
    
    case SET_RETRY_STATUS:
      return {
        ...state,
        isRetrying: action.payload.isRetrying,
        retryAttempt: action.payload.retryAttempt || 1,
        retryReason: action.payload.retryReason || '',
        retryOperation: action.payload.retryOperation || '',
        maxRetries: action.payload.maxRetries || state.maxRetries
      };
    
    case CLEAR_RETRY_STATUS:
      return {
        ...state,
        isRetrying: false,
        retryAttempt: 0,
        retryReason: '',
        retryOperation: null
      };
    
    case RESET_STATE:
      return initialState;
    
    default:
      return state;
  }
};

export default imageReducer;